// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The machines either side of you, heard from where you are standing (M8-21).
//
// M5-22 built the arcade's noise floor as three beds and an occasional knocker, and it was the
// right room for an empty arcade: a generated hum, chimes somewhere down the row, rain at the
// window. What it could not know is **which machines are being played**, because until M8-01 there
// was nobody else in the building. Now there is, and a room whose noise is the same whether it is
// empty or has five people in it is a room that sounds like a recording.
//
// **The same falloff as a voice, deliberately** (`voiceSpace.ts`). A machine two bays away and a
// person two bays away have to be equally far, or the room's geometry contradicts itself: a
// neighbour's knocker arriving louder than the neighbour saying "did you see that" puts the two of
// them in different rooms. So this reuses `voiceGainAt` rather than inventing a second curve, and
// the reuse is the argument rather than the saving.
//
// **An empty bay is quieter, not silent.** A pinball machine left alone runs its attract mode and
// makes a noise doing it — that is why a real arcade at 2 a.m. is not quiet. Silencing the empty
// bays would make a half-full arcade sound like a corridor, and would make the room get *quieter*
// as people left, which is true of conversation and not of machines.
//
// **What is not done here: re-simulating five neighbours.** A spectator's client can re-simulate
// one trace exactly (M8-19); doing it five more times to know when somebody's pop bumper fired
// would cost five simulations to produce a sound nobody can pick out at three metres. Standing in
// an arcade you do not hear a neighbour's individual switches — you hear that their machine is
// busy, and you hear their knocker. That is what this produces.
//
// Pure arithmetic: no audio nodes, no clock. `arcadeAmbience.ts` is handed the answers.

import { voiceGainAt } from './voiceSpace'

/** Where a bay is, as this module needs it — the standing spot, which is where the noise is. */
export interface BayPoint {
  readonly index: number
  readonly standX: number
  readonly standZ: number
}

/** One neighbouring machine, as it sounds from where the player is standing. */
export interface BaySound {
  readonly bay: number
  /** 0 … 1, before the ambience bed's own level and the duck. */
  readonly level: number
  /** −1 … 1, the stereo placement along the row. */
  readonly pan: number
  /** Somebody is playing it. An empty bay is in attract and quieter. */
  readonly occupied: boolean
}

/**
 * How loud an unplayed machine is against a played one.
 *
 * 0.35. An attract loop is a chime every few seconds and a light show; a game in progress is
 * bumpers, ramps and a callout. The ratio is what makes an occupied room recognisably busier
 * without making an empty one dead.
 */
export const ATTRACT_SHARE = 0.35

/**
 * How far apart the row sounds, in metres of lateral offset for full stereo.
 *
 * 2.0 — a little under two bay spacings. **Panning by the raw offset would put the machine next
 * door almost centred**, because 1.15 m is not far, and the row would collapse into the middle of
 * the player's head. Normalising to two bays puts the immediate neighbours at about 0.6 and
 * everything beyond them hard to one side, which is where a row of machines against a wall
 * actually sits when you stand in front of one of them.
 */
export const PAN_SPAN_M = 2.0

/**
 * What every bay in the room sounds like from the player's own.
 *
 * The player's own bay is excluded: their machine is the thing they are listening to and it is
 * already in the mix at full level. Adding it here would be the same machine twice, a few
 * milliseconds apart.
 */
export function baySounds(
  bays: readonly BayPoint[],
  playerBay: number,
  occupied: ReadonlySet<number>,
): readonly BaySound[] {
  const here = bays.find((bay) => bay.index === playerBay)
  if (here === undefined) return []
  const sounds: BaySound[] = []
  for (const bay of bays) {
    if (bay.index === playerBay) continue
    const dx = bay.standX - here.standX
    const dz = bay.standZ - here.standZ
    const distance = Math.sqrt(dx * dx + dz * dz)
    const busy = occupied.has(bay.index)
    sounds.push({
      bay: bay.index,
      level: voiceGainAt(distance) * (busy ? 1 : ATTRACT_SHARE),
      pan: panFor(dx),
      occupied: busy,
    })
  }
  return sounds
}

/** Where along the row a machine sits, as a stereo pan. */
export function panFor(dx: number): number {
  const pan = dx / PAN_SPAN_M
  return pan < -1 ? -1 : pan > 1 ? 1 : pan
}

/**
 * How loud the chimes bed should be, and where the room is.
 *
 * **Measured against the same room with every machine in use, rather than summed**, and that is
 * the whole of the arithmetic. A sum is unbounded — five neighbours are not a room five times as
 * loud as one — and clamping a sum to one makes a half-full arcade and a full one identical, which
 * is the bug this had on its first outing. Dividing by the busiest that row could possibly be
 * gives a number that runs from `ATTRACT_SHARE` for an empty arcade to exactly one for a full one,
 * whatever size the venue is and wherever in it the player is standing. The bed's own `BED_LEVELS`
 * entry decides how loud "one" actually is.
 *
 * The pan is the neighbours' weighted direction, so an arcade where everybody is standing to your
 * left is a room that is louder on your left.
 */
export function roomFrom(sounds: readonly BaySound[]): { readonly level: number; readonly pan: number } {
  let total = 0
  let busiest = 0
  let weighted = 0
  for (const sound of sounds) {
    total += sound.level
    busiest += sound.occupied ? sound.level : sound.level / ATTRACT_SHARE
    weighted += sound.level * sound.pan
  }
  if (total <= 0 || busiest <= 0) return { level: 0, pan: 0 }
  return { level: total / busiest, pan: weighted / total }
}

/**
 * Which bay the next knocker comes from.
 *
 * **Biased to the machines somebody is playing**, because a knocker is a replay or an extra ball
 * and an attract loop awards neither. An empty room still produces one occasionally — a machine
 * finishing its attract cycle — which is what keeps a quiet arcade from being a silent one.
 *
 * `roll` is 0 … 1 from the caller's RNG, so this stays clockless and deterministic. `undefined`
 * when there is nowhere for one to come from, which is a venue of one bay.
 */
export function knockerBay(sounds: readonly BaySound[], roll: number): BaySound | undefined {
  if (sounds.length === 0) return undefined
  const busy = sounds.filter((sound) => sound.occupied)
  const pool = busy.length > 0 ? busy : sounds
  const at = Math.min(pool.length - 1, Math.max(0, Math.floor(roll * pool.length)))
  return pool[at]
}

/**
 * How long until the next knocker, scaled by how busy the room is.
 *
 * A fuller arcade is a noisier one — five games in progress produce five times the replays — so
 * the gap shortens with the number of occupied bays. Bounded below at a third of the base gap:
 * past that it stops being "the occasional knocker from another bay" (06 §4) and becomes a rhythm,
 * and a rhythm is the one thing a noise floor must never have.
 */
export function knockerGapScale(occupiedCount: number): number {
  const scale = 1 / (1 + occupiedCount * 0.5)
  return scale < 1 / 3 ? 1 / 3 : scale
}
